import { forwardRef, useState, useRef, useEffect, useMemo, useId } from 'react';
import { IonDatetime, IonPopover } from '@ionic/react';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { AllieDateTimeInputProps } from './DateTimeInput.types';
import { AllieCalendarOutline } from '../../../styling/icons';
import { StyledDateTimeButton, StyledDateTimeContent } from './DateTimeInput.styles';
import './DateTimeInput.css';

dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Format a date ISO 8601 to display format using dayjs
 * Default format: "DD/MM/YYYY a las HH:MM"
 */
const formatDateTime = (
  isoString: string | undefined,
  displayFormat?: string,
  tz?: string
): string => {
  if (!isoString) return '';

  try {
    const date = tz ? dayjs(isoString).tz(tz) : dayjs(isoString);

    if (!date.isValid()) return '';

    if (displayFormat) {
      return date.format(displayFormat);
    }

    return date.format('L LT');
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Convert an ISO (UTC) value to the specified timezone to display in the picker
 * Returns the value in ISO format with the hour converted to the timezone
 */
const convertUTCToTimezone = (
  isoString: string | undefined,
  tz?: string
): string | undefined => {
  if (!isoString || !tz) return isoString;

  try {
    const date = dayjs(isoString).tz(tz);
    if (!date.isValid()) return isoString;
    // Return in ISO format with the timezone offset
    return date.format();
  } catch (error) {
    console.error('Error converting UTC to timezone:', error);
    return isoString;
  }
};

/**
 * Convert a selected value from the timezone back to UTC (ISO)
 * The value comes from IonDatetime. We interpret it as if it were in the specified timezone.
 */
const convertTimezoneToUTC = (
  isoString: string | undefined,
  tz: string
): string | undefined => {
  if (!isoString || !tz) return isoString;

  try {
    // Extract the date/time part without timezone information
    // Format expected: "YYYY-MM-DDTHH:mm:ss" or "YYYY-MM-DDTHH:mm:ss.sss" or with timezone
    const match = isoString.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d{3})?)/);
    if (!match) return isoString;

    const dateTimeStr = match[1];
    // Interpret the date/time as if it were in the specified timezone
    const date = dayjs.tz(dateTimeStr, tz);

    if (!date.isValid()) return isoString;

    return date.utc().toISOString();
  } catch (error) {
    console.error('Error converting timezone to UTC:', error);
    return isoString;
  }
};

/**
 * Convert max/min from UTC to the specified timezone
 */
const convertMaxMinToTimezone = (
  value: string | undefined,
  tz?: string
): string | undefined => {
  if (!value || !tz) return value;

  try {
    const date = dayjs(value).tz(tz);
    if (!date.isValid()) return value;
    return date.format();
  } catch (error) {
    console.error('Error converting max/min to timezone:', error);
    return value;
  }
};

export const AllieDateTimeInput = forwardRef<HTMLIonButtonElement, AllieDateTimeInputProps>(
  (
    {
      value,
      onIonChange,
      allieType = 'primary',
      corner = 'rounded',
      allieSize = 'normal',
      disabled = false,
      shadow = false,
      placeholder,
      displayFormat,
      datetimeProps,
      datetimeId,
      expand,
      tz,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [formattedValue, setFormattedValue] = useState<string>('');
    const popoverRef = useRef<HTMLIonPopoverElement>(null);
    const uniqueId = datetimeId || useId();

    const valueForPicker = useMemo(() => {
      return convertUTCToTimezone(value, tz);
    }, [value, tz]);

    const processedDatetimeProps = useMemo(() => {
      if (!tz || !datetimeProps) return datetimeProps;

      const processed = { ...datetimeProps };

      if (processed.max) {
        processed.max = convertMaxMinToTimezone(processed.max as string, tz);
      }

      if (processed.min) {
        processed.min = convertMaxMinToTimezone(processed.min as string, tz);
      }

      return processed;
    }, [datetimeProps, tz]);

    useEffect(() => {
      setFormattedValue(formatDateTime(value, displayFormat, tz));
    }, [value, displayFormat, tz]);

    const handleButtonClick = () => {
      if (!disabled) {
        setIsOpen(true);
      }
    };

    const handleDatetimeChange = (event: CustomEvent) => {
      const newValue = event.detail.value as string;

      // If there is a timezone, convert the selected value back to UTC
      let valueToEmit = newValue;
      if (tz && newValue) {
        valueToEmit = convertTimezoneToUTC(newValue, tz) || newValue;
      }

      const modifiedEvent = {
        ...event,
        detail: {
          ...event.detail,
          value: valueToEmit,
        },
      } as CustomEvent;

      if (onIonChange) {
        onIonChange(modifiedEvent);
      }

      setFormattedValue(formatDateTime(valueToEmit, displayFormat, tz));
    };

    const handleCancel = () => {
      popoverRef.current?.dismiss();
    };

    const displayText = formattedValue || placeholder;

    return (
      <>
        <StyledDateTimeButton
          ref={ref}
          id={`datetime-button-${uniqueId}`}
          allieType={allieType}
          corner={corner}
          allieSize={allieSize}
          disabled={disabled}
          shadow={shadow}
          fill="clear"
          onClick={handleButtonClick}
          expand={expand}
          {...props}
        >
          <StyledDateTimeContent
            style={{
              color: disabled ? 'var(--allie-color-neutral-50)' : 'var(--allie-color-neutral-90)',
            }}
          >
            {displayText}
          </StyledDateTimeContent>
          <AllieCalendarOutline slot="end" />
        </StyledDateTimeButton>

        <IonPopover
          ref={popoverRef}
          trigger={`datetime-button-${uniqueId}`}
          isOpen={isOpen}
          onDidDismiss={() => setIsOpen(false)}
          keepContentsMounted={true}
          dismissOnSelect={false}
          size="auto"
          style={{
            '--width': 'auto',
            '--min-width': '320px',
          }}
        >
          <IonDatetime
            id={uniqueId}
            value={valueForPicker}
            onIonChange={handleDatetimeChange}
            onIonCancel={handleCancel}
            {...processedDatetimeProps}
          />
        </IonPopover>
      </>
    );
  }
);

AllieDateTimeInput.displayName = 'AllieDateTimeInput';
